import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

class AppVersionService {
  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Set auth token for requests
  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  /**
   * Get all app version configurations
   * @param {Object} options - Query options (limit, offset)
   * @returns {Promise<Array>} Array of app versions
   */
  async getAllVersions(options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit);
      if (options.offset) params.append('offset', options.offset);

      const response = await this.api.get(`/api/admin/app-versions?${params.toString()}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching app versions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch app versions');
    }
  }

  /**
   * Get app version by ID
   * @param {string} id - Version ID
   * @returns {Promise<Object>} App version object
   */
  async getVersionById(id) {
    try {
      const response = await this.api.get(`/api/admin/app-versions/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching app version:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch app version');
    }
  }

  /**
   * Create a new app version configuration
   * @param {Object} versionData - Version data
   * @returns {Promise<Object>} Created version object
   */
  async createVersion(versionData) {
    try {
      const response = await this.api.post('/api/admin/app-versions', versionData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating app version:', error);
      throw new Error(error.response?.data?.error || 'Failed to create app version');
    }
  }

  /**
   * Update an app version configuration
   * @param {string} id - Version ID
   * @param {Object} versionData - Updated version data
   * @returns {Promise<Object>} Updated version object
   */
  async updateVersion(id, versionData) {
    try {
      const response = await this.api.put(`/api/admin/app-versions/${id}`, versionData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating app version:', error);
      throw new Error(error.response?.data?.error || 'Failed to update app version');
    }
  }

  /**
   * Delete an app version configuration
   * @param {string} id - Version ID
   * @returns {Promise<void>}
   */
  async deleteVersion(id) {
    try {
      await this.api.delete(`/api/admin/app-versions/${id}`);
    } catch (error) {
      console.error('Error deleting app version:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete app version');
    }
  }
}

// Export singleton instance
const appVersionService = new AppVersionService();
export default appVersionService;


